import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import Button from '@components/ui/Button/Button';
import {
  diffFormSchemas,
  type ModifiedField,
  type ModifiedProperty,
  type SchemaDiff,
} from './formSchemaDiffUtil';
import styles from './FormSchemaDiff.module.scss';

const PROPS_INITIAL_VISIBLE = 2;

interface FormSchemaDiffProps {
  before: unknown;
  after: unknown;
  onPreview: (side: 'before' | 'after') => void;
}

export function FormSchemaDiff({ before, after, onPreview }: FormSchemaDiffProps) {
  const { t } = useTranslation();

  const diff: SchemaDiff = useMemo(() => diffFormSchemas(before, after), [before, after]);

  const isEmpty =
    diff.added.length === 0 &&
    diff.removed.length === 0 &&
    diff.modified.length === 0 &&
    diff.reordered.length === 0;

  return (
    <div className={styles.body}>
      {isEmpty ? (
        <div className={styles.empty}>
          {t('K_AUDIT_FORM_NO_CHANGES', 'No structural changes.')}
        </div>
      ) : (
        <>
          {diff.added.length > 0 && (
            <DiffSection heading={t('K_AUDIT_FORM_ADDED', 'Added')} count={diff.added.length}>
              {diff.added.map((f) => (
                <li key={f.path} className={`${styles.entry} ${styles.entryAdded}`}>
                  <span className={`${styles.glyph} ${styles.glyphAdded}`} aria-hidden="true">
                    +
                  </span>
                  <span className={styles.label}>{f.label}</span>
                  <span className={styles.typeTag}>{f.type}</span>
                  {f.path !== f.key && <span className={styles.path}>{f.path}</span>}
                </li>
              ))}
            </DiffSection>
          )}

          {diff.removed.length > 0 && (
            <DiffSection heading={t('K_AUDIT_FORM_REMOVED', 'Removed')} count={diff.removed.length}>
              {diff.removed.map((f) => (
                <li key={f.path} className={`${styles.entry} ${styles.entryRemoved}`}>
                  <span className={`${styles.glyph} ${styles.glyphRemoved}`} aria-hidden="true">
                    −
                  </span>
                  <span className={styles.label}>{f.label}</span>
                  <span className={styles.typeTag}>{f.type}</span>
                  {f.path !== f.key && <span className={styles.path}>{f.path}</span>}
                </li>
              ))}
            </DiffSection>
          )}

          {diff.modified.length > 0 && (
            <DiffSection
              heading={t('K_AUDIT_FORM_MODIFIED', 'Modified')}
              count={diff.modified.length}
            >
              {diff.modified.map((f) => (
                <ModifiedRow key={f.path} field={f} />
              ))}
            </DiffSection>
          )}

          {diff.reordered.length > 0 && (
            <DiffSection
              heading={t('K_AUDIT_FORM_REORDERED', 'Reordered')}
              count={diff.reordered.length}
            >
              {diff.reordered.map((f) => (
                <li key={f.path} className={`${styles.entry} ${styles.entryReordered}`}>
                  <span
                    className={`${styles.glyph} ${styles.glyphReordered}`}
                    aria-hidden="true"
                  >
                    ↕
                  </span>
                  <span className={styles.label}>{f.label}</span>
                  <span className={styles.path}>
                    {t('K_AUDIT_FORM_REORDER_POSITIONS', 'moved from position {from} → {to}')
                      .replace('{from}', String(f.beforeIndex + 1))
                      .replace('{to}', String(f.afterIndex + 1))}
                  </span>
                </li>
              ))}
            </DiffSection>
          )}
        </>
      )}

      {!isEmpty && (
        <div className={styles.actions}>
          <Button variant="filled" onClick={() => onPreview('after')}>
            {t('K_AUDIT_FORM_PREVIEW', 'Preview form')}
          </Button>
        </div>
      )}
    </div>
  );
}

interface DiffSectionProps {
  heading: string;
  count: number;
  children: ReactNode;
}

function DiffSection({ heading, count, children }: DiffSectionProps) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeading}>
        <span>{heading}</span>
        <span className={styles.count}>({count})</span>
      </div>
      <ul className={styles.list}>{children}</ul>
    </div>
  );
}

function ModifiedRow({ field }: { field: ModifiedField }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);
  const visible = expanded ? field.changes : field.changes.slice(0, PROPS_INITIAL_VISIBLE);
  const remaining = field.changes.length - visible.length;

  return (
    <li className={`${styles.entry} ${styles.entryModified}`}>
      <span className={`${styles.glyph} ${styles.glyphModified}`} aria-hidden="true">
        ~
      </span>
      <span className={styles.label}>{field.label}</span>
      <span className={styles.typeTag}>{field.type}</span>
      {field.path !== field.key && <span className={styles.path}>{field.path}</span>}
      <div className={styles.modifiedDetails}>
        {visible.map((p) => (
          <PropertyChange key={p.property} change={p} />
        ))}
        {remaining > 0 && !expanded && (
          <button type="button" className={styles.toggle} onClick={() => setExpanded(true)}>
            {t('K_AUDIT_FORM_MORE_PROPS', '+{n} more properties').replace(
              '{n}',
              String(remaining),
            )}
          </button>
        )}
        {expanded && field.changes.length > PROPS_INITIAL_VISIBLE && (
          <button type="button" className={styles.toggle} onClick={() => setExpanded(false)}>
            {t('K_AUDIT_SHOW_LESS', 'Show less')}
          </button>
        )}
      </div>
    </li>
  );
}

function PropertyChange({ change }: { change: ModifiedProperty }) {
  return (
    <div className={styles.propRow}>
      <span className={styles.propName}>{change.property}:</span>
      <span className={styles.propValue}>{formatValue(change.before)}</span>
      <span className={styles.propArrow} aria-hidden="true">
        →
      </span>
      <span className={styles.propValue}>{formatValue(change.after)}</span>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'string') return v.length > 0 ? `"${v}"` : '""';
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === 'object') return '{…}';
  return String(v);
}
